import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { geminiGenerateJson } from '../../geminiService';
import { AI_PIPELINE_SCHEMA_V1 } from '../../../constants/aiPipelineModes';
import { normalizeStationAnalysisVerifications } from '../../../utils/normalizeVerification';
import { computeOverallMaturityScore } from '../../../utils/overallMaturityScore';

/**
 * Schema v1 report draft — verification against distilled silo / schema snapshot.
 * Ringfenced; legacy generateReportDraft body remains unchanged in adminServices.
 *
 * @param {{ schoolId: string, user: object, targetLang?: string, deps: {
 *   migrateReportStaffWorkspaceFromLegacyIfNeeded: Function,
 *   logAction: Function,
 *   reportStaffWorkspaceRef?: Function
 * }}} args
 */
export async function generateReportDraftSchemaV1({ schoolId, user, targetLang = 'English', deps }) {
  const { migrateReportStaffWorkspaceFromLegacyIfNeeded, logAction } = deps;
  const wsRefFn = deps.reportStaffWorkspaceRef;
  if (typeof wsRefFn !== 'function') throw new Error('reportStaffWorkspaceRef required in deps');

  const schoolRef = doc(db, 'schools', schoolId);
  const auditRef = doc(db, 'audits', schoolId);
  const reportRef = doc(db, 'reports', schoolId);
  const wsRef = wsRefFn(schoolId);
  const distilledRef = doc(db, 'distilledData', schoolId);

  await migrateReportStaffWorkspaceFromLegacyIfNeeded(schoolId);

  const [schoolSnap, auditSnap, reportSnap, distilledSnap, wsSnap] = await Promise.all([
    getDoc(schoolRef),
    getDoc(auditRef),
    getDoc(reportRef),
    getDoc(distilledRef),
    getDoc(wsRef),
  ]);

  const schoolRaw = schoolSnap.data();
  const schoolData = schoolRaw?.answers || {};
  const auditData = auditSnap.data() || { stations: {}, evidenceVault: [] };
  const fullReportData = reportSnap.data() || {};
  const wsData = wsSnap.exists() ? wsSnap.data() : {};
  const contactLogForPrompt =
    Array.isArray(wsData.accreditorContactLog) && wsData.accreditorContactLog.length
      ? wsData.accreditorContactLog
      : fullReportData?.meta?.accreditorContactLog || [];
  const internalPrecisForPrompt =
    typeof wsData.internalNotes === 'string' && wsData.internalNotes.trim() !== ''
      ? wsData.internalNotes
      : typeof fullReportData.internalNotes === 'string'
        ? fullReportData.internalNotes
        : '';

  const distilledData = distilledSnap.exists()
    ? distilledSnap.data()
    : { factSheet: [], anonymizedSummary: '' };

  const accreditationLevel = String(schoolRaw?.accreditationLevel || 'foundation').toLowerCase();
  const schemaSnapshot = distilledData.accreditationSchema || distilledData.schemaSnapshot || null;
  const elementCoverage =
    distilledData.policyElementCoverage ||
    schemaSnapshot?.policy?.element_coverage ||
    null;

  if (!fullReportData.meta?.authorUid) {
    await updateDoc(reportRef, { 'meta.authorUid': user.uid });
  }

  const prompt = `
SYSTEM: You are the IFIP Lead Accreditation Auditor (schema_v1 verification pipeline).
Generate a definitive formal report for ${schoolRaw?.officialName}.
PIPELINE: ${AI_PIPELINE_SCHEMA_V1} — evidence VERIFIES claims; do not weave complementary storytelling.

MANDATORY: Entire report in ${targetLang}.

INPUT DATA:
0. ACCREDITATION LEVEL (labels/context only — same verification rules all tiers): ${accreditationLevel}
1. INSTITUTIONAL DNA: ${JSON.stringify(schoolData)}
2. SELF-EVALUATION (SEF): ${JSON.stringify(auditData.stations)}
3. EVIDENCE REPOSITORY (filenames): ${(auditData.evidenceVault || []).map((f) => f.name).join(', ')}
4. Silo A / DISTILLED EVIDENCE (title-based until per-file extract ships): ${JSON.stringify(distilledData)}
4b. SCHEMA / POLICY ELEMENT COVERAGE (if present): ${JSON.stringify(elementCoverage || schemaSnapshot || null)}
5. ACCREDITOR CONTACT / EXCHANGE LOG (always include when present — all tiers): ${JSON.stringify(contactLogForPrompt)}
6. INTERNAL ACCREDITOR PRECIS (always include when present — all tiers): ${JSON.stringify(internalPrecisForPrompt || '')}
7. DISCIPLINARY EXCLUSIONS: schoolExclusions in DNA — weave where material.

STRICT AUDIT GUIDELINES (schema_v1):
- EVIDENCE ROLE: Silo A / schema VERIFY claims. Not complementary colour for the school story.
- SAFETY LOGIC: If Silo A / schema is missing or weak, do NOT treat the school narrative as verified. Flag "Verification Deficit" in internalNotes and evidence gaps. Propose cautious IFIP ratings where claims lack support.
- ALL TIERS: Always use accreditor contact log + precis when present. Prefer accreditor over school on conflict; record contradiction. For material school claims the accreditor did not address, list DISCONNECT lines in internalNotes. Do not fill school-facing text from uncorroborated school-only claims the accreditor left silent.
- INDEPENDENT JUDGMENT: If distilled anchors/gaps or schema grading_status contradict self-selected levels, override with a clinical justified 1-5.
- Station 5 / policy: treat policy element grading_status (when present) as verification of policy claims; advisory_gaps are informational only — do not fail the school on advisory gaps alone.
- Photographs in the vault are human-review-only; never treat them as AI-verified content.
- EVIDENCE PORTFOLIO: clinical mirror of vault using Silo A; set gaps for missing/weak verification.
- STRATEGIC ROADMAP: synthesis paragraphs then numbered recommendations (at least three where appropriate); include IFIP service menu items where suitable.
- PUBLIC PORTRAIT: 250-350 word shareable pen portrait; only what evidence supports; thin evidence → careful general language or silence; no station names; omit development points.
- TONE: Professional, clinical. No em dashes or bold.

OUTPUT: Return ONLY raw JSON:
{
  "institutionalBrief": { "executiveSummary": "250-350 word public pen portrait" },
  "stationAnalysis": {
    "01": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "02": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "03": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "04": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "05": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "06": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "07": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } }
  },
  "evidencePortfolio": {
    "registry": [ { "fileName": "", "category": "", "evidenceSummary": "", "proves": "", "strength": 1 } ],
    "gaps": ""
  },
  "strategicRoadmap": { "content": "", "nextPhase": "", "resources": "" },
  "internalNotes": "Verification deficits, DISCONNECT lines, advisory gaps (informational), contradictions"
}

CRITICAL: verification.anchors and verification.gaps MUST be arrays of short plain strings only — never factSheet/registry objects.
`;

  const reportContent = await geminiGenerateJson(prompt);

  if (!reportContent.evidencePortfolio || typeof reportContent.evidencePortfolio !== 'object') {
    reportContent.evidencePortfolio = { registry: [], gaps: '' };
  }
  if (!Array.isArray(reportContent.evidencePortfolio.registry)) {
    reportContent.evidencePortfolio.registry = [];
  }
  normalizeStationAnalysisVerifications(reportContent.stationAnalysis);

  const avgScore = computeOverallMaturityScore(reportContent.stationAnalysis);

  const aiInternalPrecis = reportContent.internalNotes;
  delete reportContent.internalNotes;

  await updateDoc(reportRef, {
    ...reportContent,
    'meta.isGenerated': true,
    'meta.generatedAt': serverTimestamp(),
    'meta.overallMaturityScore': avgScore,
    'meta.aiPipeline': AI_PIPELINE_SCHEMA_V1,
    workflowStep: 'ai_draft_ready',
  });

  if (aiInternalPrecis !== undefined && aiInternalPrecis !== null) {
    await setDoc(
      wsRef,
      { internalNotes: String(aiInternalPrecis), updatedAt: serverTimestamp() },
      { merge: true }
    );
  }

  await logAction('AI/System', 'REPORT_GENERATION_COMPLETE', {
    schoolId,
    pipeline: AI_PIPELINE_SCHEMA_V1,
  });
}